import { Controller } from "react-hook-form";
import type { Control, FieldValues, Path } from "react-hook-form";
import { FormControl, FormHelperText, MenuItem, Select, Stack } from "@mui/material";
import type { SelectProps } from "@mui/material";
import { FieldLabel } from "./FieldLabel";

interface Option {
  value: string;
  label: string;
}

interface Props<T extends FieldValues> extends Omit<SelectProps<string>, "name" | "error"> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  required?: boolean;
  options: readonly Option[];
  helperText?: string;
  maxWidth?: number | string;
}

export function LabeledSelect<T extends FieldValues>(
  props: Props<T>,
): React.ReactElement {
  const { name, control, label, required, options, helperText, maxWidth = 600, ...rest } = props;
  return (
    <Stack sx={{ maxWidth, width: "100%" }}>
      <FieldLabel label={label} required={required === true} htmlFor={name} />
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = fieldState.error !== undefined;
          const message = fieldState.error?.message ?? helperText;
          return (
            <FormControl fullWidth error={hasError} required={required === true}>
              <Select
                {...rest}
                {...field}
                id={name}
                required={required === true}
                value={typeof field.value === "string" ? field.value : ""}
              >
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {message !== undefined && message !== "" ? (
                <FormHelperText>{message}</FormHelperText>
              ) : null}
            </FormControl>
          );
        }}
      />
    </Stack>
  );
}
